import { Show, createSignal } from "solid-js";
import * as api from "../api";
import type { Queue } from "../game/queue";
import { lastOptions } from "../game/options";
import { holdFocus } from "./hold";
import { t } from "../strings";

const clockOf = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// The ranked queue's bar (design 14 §6.3), on every page while queued: the length, the time waited, how many are
// searching, the bot game to play meanwhile, Cancel. While a table forms for a player in a bot game, the notice covers
// the table with its ten seconds and "Join now".
export function QueueBar(props: { queue: Queue; path: string; go: (p: string) => void }) {
  const q = props.queue;
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const atTable = () => props.path.startsWith("/t/");
  const bots = () => {
    const o = lastOptions(true);
    setBusy(true);
    setError("");
    api.createTable(o.rules, o.length, o.clock, true).then(
      (tb) => {
        setBusy(false);
        props.go(`/t/${tb.token}`);
      },
      (err: unknown) => {
        setBusy(false);
        if (err instanceof api.ApiError && typeof err.body.token === "string") props.go(`/t/${err.body.token}`);
        else setError(err instanceof Error ? err.message : String(err));
      },
    );
  };
  return (
    <Show when={q.state()?.queued ? q.state() : null}>
      {(s) => (
        <>
          <Show when={s().forming && !s().coming}>
            <div class="queue-notice" role="alertdialog" aria-label={t.tableReady} ref={(el) => holdFocus(el)}>
              <div class="queue-notice-card">
                <strong>{t.tableReady}</strong>
                <span>{t.joiningIn(Math.ceil(q.startsIn() / 1000))}</span>
                <button type="button" onClick={() => q.joinNow()}>
                  {t.joinNow}
                </button>
              </div>
            </div>
          </Show>
          <aside class={`queue-bar${atTable() ? " at-table" : ""}`} aria-live="polite">
            <Show
              when={!s().forming}
              fallback={
                <span class="queue-text">
                  <strong>{t.tableFound}</strong>
                </span>
              }
            >
              <span class="queue-text">
                <strong>{s().length === "east" ? t.eastShort : t.eastSouthShort}</strong> · <span class="queue-clock">{clockOf(q.waited())}</span>
                <Show when={(s().searching[s().length!] ?? 0) > 1}>
                  {" "}· <span class="small">{t.searchingCount(s().searching[s().length!] ?? 0)}</span>
                </Show>
              </span>
              <Show when={!atTable()}>
                <button type="button" class="link" disabled={busy()} onClick={bots}>
                  {t.botsWhileWaiting}
                </button>
              </Show>
              <button type="button" class="link" onClick={() => q.leave()}>
                {t.cancel}
              </button>
            </Show>
            <Show when={q.askNotify()}>
              <span class="queue-ask">
                {t.notifyAsk}{" "}
                <button type="button" class="link" onClick={() => q.answerNotify(true)}>
                  {t.notifyYes}
                </button>{" "}
                <button type="button" class="link" onClick={() => q.answerNotify(false)}>
                  {t.notifyNo}
                </button>
              </span>
            </Show>
            <Show when={error()}>
              <span class="queue-ask">{error()}</span>
            </Show>
          </aside>
        </>
      )}
    </Show>
  );
}
